import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';
import {
  getPlan,
  getProductByPlanId,
  getPlanById,
  getAddonsByIds,
  getAddonsIds,
  getTotalPrice,
} from '@/utility/subscription';
import store from '@/utility/store';
import {
  analyticsSelectPlan,
  analyticsClickedintoAddonSelectBox,
  analyticsClickedAddon,
  analyticsChoosePlanAndAddons,
} from '@/utility/analytics';

// STORE
import { SubscriptionActions } from '@/store/actions';

// HOC
import withStripeElements from '@/hoc/withStripeElements';

// COMPONENTS
import ChannelCard from '@/components/Cards/ChannelCard';

// HELP
import PlanCard from './help/PlanCard';
import MultiselectSearch from './help/MultiselectSearch';

// STYLES
import { Container, ChannelsGrid, DialogChannels } from './styles';

function SubscriptionPlanForm({ onNext, dictionary, analyticsLocation, type }) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [channelDialogState, setChannelDialogState] = useState({ isOpen: false, data: {} });
  const curChannels = channelDialogState.data?.channels || [];
  const { subscription, selectedProduct, selectedPlan, selectedAddons } = dictionary;
  const products = subscription.products || [];
  const firstPlan = (products.length && getPlan(products[0])) || {};
  const initPlanId = selectedPlan?.id?.toString() || firstPlan.id?.toString() || '';
  const initAddons = (selectedAddons && getAddonsIds(selectedAddons)) || [];
  const merchantType = subscription?.merchant?.slug;
  const isSlingType = merchantType === 'sling-tv' || merchantType === 'sling-latino';
  const isSR = type === 'sr';

  function handleUpdateDictionary(name, value) {
    let data;

    switch (name) {
      case 'plan_id': {
        const newSelectedProduct = getProductByPlanId(products, value);
        const newSelectedPlan = getPlanById(newSelectedProduct.plans, value);

        data = {
          selectedProduct: newSelectedProduct,
          selectedPlan: newSelectedPlan,
          selectedAddons: [],
          totalPrice: getTotalPrice(newSelectedPlan, null),
        };

        analyticsSelectPlan({
          location: analyticsLocation,
          product_id: newSelectedProduct.id,
          product_name: newSelectedProduct.name,
          plan_id: newSelectedPlan.id,
          plan_name: newSelectedPlan.name,
          plan_price: newSelectedPlan.price,
          previous_plan_id: selectedPlan.id,
        });

        break;
      }
      case 'addons': {
        const newSelectedAddons = value.length
          ? getAddonsByIds(selectedProduct.addOns, value)
          : null;

        data = {
          selectedAddons: newSelectedAddons,
          totalPrice: getTotalPrice(selectedPlan, newSelectedAddons),
        };

        break;
      }

      default:
        data = {};
        break;
    }

    dispatch(SubscriptionActions.updateSubscriptionDictionary.action({ data }));
  }

  function handleSetFieldValue(name, value, setFieldValue) {
    setFieldValue(name, value);
    handleUpdateDictionary(name, value);
  }

  function handleChangeValue(e, handleChange, setFieldValue) {
    const { name, value } = e.target;

    handleChange(e);

    handleUpdateDictionary(name, value);

    if (name === 'plan_id') {
      setFieldValue('addons', []);
    }
  }

  function onSubmit(values) {
    if (!isSR) store.set('subscription', { merchant_slug: subscription.merchant.slug, ...values });

    const {
      selectedProduct: { id: product_id },
      selectedPlan: { id: plan_id },
      selectedAddons: selected_addons,
      totalPrice,
    } = dictionary;

    const addons = selected_addons
      ? selected_addons.map(addon => ({
          addon_id: addon.id,
          addon_plan_id: addon.plans[0].id,
        }))
      : [];

    analyticsChoosePlanAndAddons({
      location: analyticsLocation,
      app_id: dictionary.app.id,
      product_id,
      plan_id,
      addons,
      price: totalPrice,
    });

    onNext();
  }

  function handleChangeChannelsDialogState(state, data = {}) {
    setChannelDialogState({
      isOpen: state,
      data,
    });
  }

  return (
    <Formik
      initialValues={{ plan_id: initPlanId, addons: initAddons }}
      validationSchema={setYupValidationSchema('subscriptionPlanSchema', t)}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ isValid, handleChange, handleBlur, values, setFieldValue }) => {
        const curSelectedPlan = getProductByPlanId(products, values.plan_id);
        const addons = (curSelectedPlan && curSelectedPlan.addOns) || [];

        return (
          <Container>
            <Form>
              {products.map(product => {
                const plan = getPlan(product);

                if (!plan) return null;

                return (
                  <PlanCard
                    name="plan_id"
                    value={`${plan.id}`}
                    checkedValue={values.plan_id}
                    data={product}
                    plan={plan}
                    handleChange={e => handleChangeValue(e, handleChange, setFieldValue)}
                    onSeeChannelsClick={handleChangeChannelsDialogState}
                    handleBlur={handleBlur}
                    isSlingType={isSlingType}
                    key={product.id}
                  />
                );
              })}
              {!isSR && !!addons.length && (
                <div className="addons m-t-30">
                  <Typography className="m-b-10" variant="h6">
                    {t('common.addOns')}
                    &nbsp;
                    <Typography
                      className="p4 text-lowercase"
                      component="span"
                      color="textSecondary"
                    >
                      ({t('common.optional')})
                    </Typography>
                  </Typography>
                  <MultiselectSearch
                    name="addons"
                    placeholder={t('inputs.searchBy', { name: t('common.addOn') })}
                    data={addons}
                    selected={values.addons}
                    selectedAddons={selectedAddons || []}
                    onChange={(name, value) => handleSetFieldValue(name, value, setFieldValue)}
                    onSeeChannelsClick={handleChangeChannelsDialogState}
                    onOpenCallback={() => {
                      analyticsClickedintoAddonSelectBox({
                        location: analyticsLocation,
                        product_id: curSelectedPlan.id,
                        plan_id: curSelectedPlan.plans[0].id,
                      });
                    }}
                    onAddonСlick={data => {
                      analyticsClickedAddon({
                        location: analyticsLocation,
                        product_id: curSelectedPlan.id,
                        product_name: curSelectedPlan.name,
                        plan_id: curSelectedPlan.plans[0].id,
                        ...data,
                      });
                    }}
                  />
                </div>
              )}
              <Button
                className="sizeExtraLarge"
                type="submit"
                disabled={!isValid}
                color="primary"
                fullWidth
              >
                {t('buttons.confirmSubscriptionPlan')}
              </Button>
              {isSlingType && (
                <Typography className="m-t-20 p5">{t('subscription.extra_cost_info')}</Typography>
              )}
            </Form>
            <DialogChannels
              open={channelDialogState.isOpen}
              onClose={() => handleChangeChannelsDialogState(false)}
              title={`${channelDialogState.data?.name || ''} ${t('common.channelList')}`}
              maxWidth="md"
              TransitionProps={{
                timeout: 0,
              }}
              hideConfirmButton
              hideCancelButton
            >
              <ChannelsGrid container spacing={1}>
                {!!curChannels.length && (
                  <>
                    {curChannels.map(channel => (
                      <Grid item xs={3} sm={2} md={1} key={channel.id}>
                        <ChannelCard data={channel} hover active />
                      </Grid>
                    ))}
                  </>
                )}
              </ChannelsGrid>
            </DialogChannels>
          </Container>
        );
      }}
    </Formik>
  );
}

SubscriptionPlanForm.defaultProps = {
  type: 'default',
};

SubscriptionPlanForm.propTypes = {
  dictionary: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  analyticsLocation: PropTypes.string,
  type: PropTypes.oneOf(['default', 'sr']),
};

export default withStripeElements(SubscriptionPlanForm);
